'use client'

import { useEffect, useState } from 'react'
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Link2,
  Strikethrough,
  Underline,
} from 'lucide-react'

const TEXT_COLORS = [
  { value: '#f8fafc', label: 'White' },
  { value: '#fbbf24', label: 'Amber' },
  { value: '#34d399', label: 'Green' },
  { value: '#60a5fa', label: 'Blue' },
  { value: '#f87171', label: 'Red' },
]

function stopSelectBlur(e) {
  e.stopPropagation()
}

function keepSelection(e) {
  e.preventDefault()
}

function normalizeHexColor(color) {
  if (!color || typeof color !== 'string') return ''
  const c = color.trim().toLowerCase()
  const preset = TEXT_COLORS.find((p) => p.value === c)
  if (preset) return preset.value
  if (/^#[0-9a-f]{6}$/i.test(c)) return c
  const rgb = c.match(
    /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i,
  )
  if (rgb) {
    const hex = [rgb[1], rgb[2], rgb[3]]
      .map((n) => Number(n).toString(16).padStart(2, '0'))
      .join('')
    const fromRgb = `#${hex}`
    return TEXT_COLORS.find((p) => p.value === fromRgb)?.value ?? fromRgb
  }
  return ''
}

function readTextColor(editor) {
  const raw = editor.getAttributes('textStyle')?.color
  return normalizeHexColor(raw)
}

function applyTextColor(editor, color) {
  const chain = editor.chain().focus()
  if (color) {
    chain.extendMarkRange('textStyle').setColor(color).run()
  } else {
    chain.extendMarkRange('textStyle').unsetColor().run()
  }
}

/**
 * Bubble menu toolbar — matches nb-bubble-toolbar styling.
 */
export function NotebookBubbleToolbar({ editor }) {
  const [, setTick] = useState(0)

  useEffect(() => {
    if (!editor) return undefined
    const bump = () => setTick((n) => n + 1)
    editor.on('selectionUpdate', bump)
    editor.on('transaction', bump)
    return () => {
      editor.off('selectionUpdate', bump)
      editor.off('transaction', bump)
    }
  }, [editor])

  if (!editor) return null

  const currentColor = readTextColor(editor)

  const setLink = () => {
    const prev = editor.getAttributes('link').href
    const url = window.prompt('Link URL', prev ?? 'https://')
    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  return (
    <div
      role="toolbar"
      aria-label="Text formatting"
      className="nb-bubble-toolbar"
      onMouseDown={(e) => {
        const tag = e.target?.tagName?.toLowerCase?.()
        if (tag === 'button' || tag === 'option') return
        if (e.target?.closest?.('button')) return
        keepSelection(e)
      }}
    >
      <button
        type="button"
        title="Bold"
        className={editor.isActive('bold') ? 'is-active' : ''}
        onMouseDown={keepSelection}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold size={15} />
      </button>
      <button
        type="button"
        title="Italic"
        className={editor.isActive('italic') ? 'is-active' : ''}
        onMouseDown={keepSelection}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic size={15} />
      </button>
      <button
        type="button"
        title="Underline"
        className={editor.isActive('underline') ? 'is-active' : ''}
        onMouseDown={keepSelection}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <Underline size={15} />
      </button>
      <button
        type="button"
        title="Strikethrough"
        className={editor.isActive('strike') ? 'is-active' : ''}
        onMouseDown={keepSelection}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough size={15} />
      </button>
      <span className="nb-bubble-toolbar__sep" />
      <div
        className="nb-bubble-toolbar__colors"
        role="group"
        aria-label="Text color"
        onMouseDown={stopSelectBlur}
      >
        <button
          type="button"
          title="Default text color"
          className={`nb-bubble-toolbar__swatch nb-bubble-toolbar__swatch--default${!currentColor ? ' is-active' : ''}`}
          onMouseDown={keepSelection}
          onClick={() => applyTextColor(editor, '')}
        >
          <span className="nb-bubble-toolbar__swatch-inner" />
        </button>
        {TEXT_COLORS.map((c) => (
          <button
            key={c.value}
            type="button"
            title={c.label}
            className={`nb-bubble-toolbar__swatch${currentColor === c.value ? ' is-active' : ''}`}
            onMouseDown={keepSelection}
            onClick={() => applyTextColor(editor, c.value)}
          >
            <span
              className="nb-bubble-toolbar__swatch-inner"
              style={{ backgroundColor: c.value }}
            />
          </button>
        ))}
      </div>
      <span className="nb-bubble-toolbar__sep" />
      <button
        type="button"
        title="Align left"
        className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
        onMouseDown={keepSelection}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
      >
        <AlignLeft size={15} />
      </button>
      <button
        type="button"
        title="Align center"
        className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
        onMouseDown={keepSelection}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
      >
        <AlignCenter size={15} />
      </button>
      <button
        type="button"
        title="Align right"
        className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
        onMouseDown={keepSelection}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
      >
        <AlignRight size={15} />
      </button>
      <span className="nb-bubble-toolbar__sep" />
      <button
        type="button"
        title="Link"
        className={editor.isActive('link') ? 'is-active' : ''}
        onMouseDown={keepSelection}
        onClick={setLink}
      >
        <Link2 size={15} />
      </button>
    </div>
  )
}
